import { useCallback, useEffect, useState } from "react";
import {
  Building2,
  CalendarDays,
  CheckCircle2,
  RefreshCw,
  TrendingUp,
  WalletCards,
} from "lucide-react";
import {
  Button,
  EmptyState,
  ErrorState,
  Notice,
  Skeleton,
} from "../components/ui";
import { apiRequest } from "../lib/api";
import { formatRupiah } from "../lib/format";
import type { ApiRecord } from "../lib/types";

const get = (row: ApiRecord, ...keys: string[]) =>
  keys.map((key) => row[key]).find((value) => value !== undefined && value !== null);
const text = (value: unknown, fallback = "-") =>
  typeof value === "string" || typeof value === "number"
    ? String(value)
    : fallback;
const number = (value: unknown) =>
  Number.isFinite(Number(value)) ? Number(value) : 0;
const record = (value: unknown): ApiRecord =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as ApiRecord)
    : {};
const records = (value: unknown): ApiRecord[] =>
  Array.isArray(value) ? value.map(record) : [];

function monthLabel(value: string): string {
  const [year, month] = value.split("-").map(Number);
  if (!year || !month) return value;
  return new Intl.DateTimeFormat("id-ID", {
    month: "long",
    year: "numeric",
    timeZone: "Asia/Makassar",
  }).format(new Date(Date.UTC(year, month - 1, 1)));
}

function dateLabel(value: string): string {
  const date = new Date(`${value}T00:00:00+08:00`);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat("id-ID", {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
        timeZone: "Asia/Makassar",
      }).format(date);
}

export function ReportsPage() {
  const [days, setDays] = useState("30");
  const [branchId, setBranchId] = useState("");
  const [data, setData] = useState<ApiRecord>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  const load = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      const params = new URLSearchParams({ days });
      if (branchId) params.set("branch_id", branchId);
      setData(
        record(await apiRequest<unknown>(`/api/owner/reports?${params.toString()}`)),
      );
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Laporan belum dapat dimuat.",
      );
    } finally {
      setLoading(false);
    }
  }, [branchId, days]);

  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 0);
    return () => window.clearTimeout(timer);
  }, [load]);

  const summary = record(data?.summary);
  const branches = records(data?.branches);
  const perBranch = records(get(data ?? {}, "perBranch", "per_branch"));
  const daily = records(data?.daily);
  const monthly = records(data?.monthly);

  return (
    <div className="dashboard-page reports-page">
      <div className="dashboard-heading">
        <div>
          <span className="eyebrow">Order selesai</span>
          <h1>Laporan Penjualan</h1>
          <p>Ringkasan harian dan bulanan untuk setiap cabang.</p>
        </div>
        <Button variant="secondary" onClick={() => void load()} icon={<RefreshCw />}>
          Perbarui
        </Button>
      </div>

      <Notice tone="info">
        Semua angka di halaman ini hanya berasal dari pesanan berstatus
        <strong> Selesai</strong>. Pesanan baru, dibatalkan, dan ditolak tidak
        ikut dihitung.
      </Notice>

      <div className="toolbar reports-filter">
        <label>
          <CalendarDays aria-hidden="true" />
          <span>Periode</span>
          <select value={days} onChange={(event) => setDays(event.target.value)}>
            <option value="7">7 hari terakhir</option>
            <option value="30">30 hari terakhir</option>
            <option value="90">90 hari terakhir</option>
            <option value="180">6 bulan terakhir</option>
            <option value="366">12 bulan terakhir</option>
          </select>
        </label>
        <label>
          <Building2 aria-hidden="true" />
          <span>Cabang</span>
          <select value={branchId} onChange={(event) => setBranchId(event.target.value)}>
            <option value="">Semua cabang</option>
            {branches.map((branch) => (
              <option value={text(branch.id, "")} key={text(branch.id)}>
                {text(branch.name)}
              </option>
            ))}
          </select>
        </label>
      </div>

      {loading && <Skeleton rows={7} cards />}
      {error && <ErrorState message={error} onRetry={() => void load()} />}
      {!loading && !error && (
        <>
          <div className="report-metric-grid">
            <article>
              <CheckCircle2 aria-hidden="true" />
              <span>Pesanan selesai</span>
              <strong>{number(get(summary, "completedOrders", "completed_orders"))}</strong>
              <small>Dalam periode yang dipilih</small>
            </article>
            <article>
              <TrendingUp aria-hidden="true" />
              <span>Nilai order selesai</span>
              <strong>{formatRupiah(number(get(summary, "completedSales", "completed_sales")))}</strong>
              <small>Total seluruh cabang terpilih</small>
            </article>
            <article>
              <WalletCards aria-hidden="true" />
              <span>Rata-rata per order</span>
              <strong>{formatRupiah(number(get(summary, "averageOrderValue", "average_order_value")))}</strong>
              <small>Dihitung dari order selesai</small>
            </article>
            <article>
              <WalletCards aria-hidden="true" />
              <span>Selesai belum ditandai dibayar</span>
              <strong>{number(get(summary, "completedUnpaid", "completed_unpaid"))}</strong>
              <small>Perlu diperiksa di menu pembayaran</small>
            </article>
          </div>

          <section className="dashboard-panel">
            <div className="panel-heading">
              <div>
                <span className="eyebrow">Per cabang</span>
                <h2>Total periode</h2>
              </div>
            </div>
            {perBranch.length === 0 ? (
              <EmptyState title="Belum ada order selesai pada periode ini" />
            ) : (
              <div className="report-branch-grid">
                {perBranch.map((row) => (
                  <article key={text(get(row, "branchId", "branch_id"))}>
                    <strong>{text(get(row, "branchName", "branch_name"))}</strong>
                    <span>
                      {number(get(row, "completedOrders", "completed_orders"))} pesanan selesai
                    </span>
                    <b>{formatRupiah(number(get(row, "completedSales", "completed_sales")))}</b>
                    <small>
                      {number(get(row, "completedUnpaid", "completed_unpaid"))} belum ditandai dibayar
                    </small>
                  </article>
                ))}
              </div>
            )}
          </section>

          <section className="dashboard-panel">
            <div className="panel-heading">
              <div>
                <span className="eyebrow">Harian</span>
                <h2>Pendapatan order selesai per hari</h2>
              </div>
            </div>
            {daily.length === 0 ? (
              <EmptyState title="Belum ada laporan harian" />
            ) : (
              <div className="data-table-wrap">
                <table className="data-table report-table">
                  <thead>
                    <tr>
                      <th>Tanggal WITA</th>
                      <th>Cabang</th>
                      <th>Pesanan selesai</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {daily.map((row, index) => (
                      <tr key={`${text(get(row, "reportDate", "report_date"))}-${text(get(row, "branchId", "branch_id"))}-${index}`}>
                        <td data-label="Tanggal WITA">{dateLabel(text(get(row, "reportDate", "report_date")))}</td>
                        <td data-label="Cabang">{text(get(row, "branchName", "branch_name"))}</td>
                        <td data-label="Pesanan selesai">{number(get(row, "completedOrders", "completed_orders"))}</td>
                        <td data-label="Total"><strong>{formatRupiah(number(get(row, "completedSales", "completed_sales")))}</strong></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="dashboard-panel">
            <div className="panel-heading">
              <div>
                <span className="eyebrow">Bulanan</span>
                <h2>Pendapatan order selesai per bulan</h2>
              </div>
            </div>
            {monthly.length === 0 ? (
              <EmptyState title="Belum ada laporan bulanan" />
            ) : (
              <div className="data-table-wrap">
                <table className="data-table report-table">
                  <thead>
                    <tr>
                      <th>Bulan</th>
                      <th>Cabang</th>
                      <th>Pesanan selesai</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthly.map((row, index) => (
                      <tr key={`${text(get(row, "reportMonth", "report_month"))}-${text(get(row, "branchId", "branch_id"))}-${index}`}>
                        <td data-label="Bulan">{monthLabel(text(get(row, "reportMonth", "report_month")))}</td>
                        <td data-label="Cabang">{text(get(row, "branchName", "branch_name"))}</td>
                        <td data-label="Pesanan selesai">{number(get(row, "completedOrders", "completed_orders"))}</td>
                        <td data-label="Total"><strong>{formatRupiah(number(get(row, "completedSales", "completed_sales")))}</strong></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

